import { Component, OnInit, ViewChild } from '@angular/core';
import { Router, ActivatedRoute, Params } from '@angular/router';
import { MatSort } from '@angular/material/sort';
import { MatPaginator } from '@angular/material/paginator';
import { Store } from '@ngrx/store';
import { Observable, Subject } from 'rxjs';
import { takeUntil, map } from 'rxjs/operators';
import { AppState, Selectors } from 'store/root-reducer';
import { CustomerActionTypes } from 'store/actions/customer.actions';
import { Customer } from 'modules/shared/models/customer.models';
import { CustomerDataSource } from './customer-list-datasource';
import { CustomerService } from 'modules/core/services/customer.service';
import { SnackbarService } from 'app/services/snackbar.service';
import { environment } from 'environments/environment';
import { AdminService } from 'modules/core/services/admin.service';
import { HttpErrorResponse } from '@angular/common/http';

@Component({
  selector: 'artemis-customers-list-page',
  templateUrl: './customers-list.page.html',
  styleUrls: ['./customers-list.page.scss']
})
export class CustomersListPage implements OnInit {
  readonly customerPerPage: number = 10;
  displayedColumns: Array<string>;
  natureOfBusinessVisible: boolean;
  customers: Array<Customer>;
  count: number = 0;
  loading: Observable<boolean>;
  unsubscribe$: Subject<any> = new Subject();
  dataSource: CustomerDataSource = new CustomerDataSource(this.store);
  preparingDownload: boolean = false;
  isDownloadButtonClicked: boolean = false;
  enableHandshakes: boolean = environment['ENABLE_HANDSHAKES'];
  enableOpenCorporates: boolean = environment['ENABLE_OPENCORPORATES'];

  @ViewChild(MatSort, { static: true }) sort: MatSort;
  @ViewChild(MatPaginator, { static: true }) paginator: MatPaginator;

  constructor(
    private router: Router,
    private activeRouter: ActivatedRoute,
    private snackBar: SnackbarService,
    private customerService: CustomerService,
    private adminService: AdminService,
    private store: Store<AppState>) { }

  ngOnInit() {
    this.getLoadingStatus();
    this.getAppConfigProfile();

    this.dataSource.sort = this.sort;
    this.dataSource.paginator = this.paginator;
    this.paginator.pageSize = 10;

    this.activeRouter
      .params
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(
        (params: Params) => {
          params.name ? this.dataSource.customerName = params.name : this.dataSource.customerName = '';
          this.dataSource.loadCustomer({
            risks: params.risks,
            recordType: params.recordType,
            active: params.active,
            status: params.status,
            integrationStatus: params.integrationStatus
          }, true);
          this.dataSource.paginator.pageIndex = 0;
        }
      );
  }

  private getAppConfigProfile(): void {
    this.adminService.getConfig('PROFILE')
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(
        (res: any) => {
          this.natureOfBusinessVisible = Boolean(res.value === 'EIPL');
          if (this.natureOfBusinessVisible) {
            this.displayedColumns = [
              'is_active_customer', 'name', 'record_type', 'status',
              'nature_of_business_relationship', 'risk_rating', 'updated_at', 'updated_by', 'options'];
          } else {
            this.displayedColumns = ['is_active_customer', 'name', 'record_type', 'status', 'risk_rating', 'updated_at', 'updated_by', 'options'];
          }
        },
        (err: HttpErrorResponse) => {
          this.natureOfBusinessVisible = false;
          this.displayedColumns = ['is_active_customer', 'name', 'record_type', 'status', 'risk_rating', 'updated_at', 'updated_by', 'options'];
        }
      );
    return;
  }

  /**
   * Get loading status from store
   * @method getLoadingStatus
   */
  private getLoadingStatus(): void {
    this.loading = this.store.select(Selectors.customer.getLoading)
      .pipe(
        takeUntil(this.unsubscribe$),
        map((customerLoading: Array<CustomerActionTypes>) => {
          return customerLoading.includes(CustomerActionTypes.GET_CUSTOMERS);
        })
      );
  }

  searchCustomerList(filterValue: string): void {
    this.paginator.pageIndex = 0;
    this.dataSource.search(filterValue.trim().toLowerCase());
  }

  toViewCustomer(id: number): void {
    this.router.navigateByUrl(`/customers/${id}`);
  }

  getCsv() {
    this.preparingDownload = true;
    this.isDownloadButtonClicked = true;
    return this.customerService.getCsv().subscribe(
      (res: any) => {
        this.preparingDownload = false;
        this.snackBar.success('Compiling list of customers into a CSV file. An email will be sent to you shortly.');
        this.isDownloadButtonClicked = false;
      },
      (error: HttpErrorResponse) => {
        this.preparingDownload = false;
        this.snackBar.error('Error: Failed to download customer list. Please try again.');
        this.isDownloadButtonClicked = false;
      }
    );
  }

  downloadReport(customer: Customer): void {
    if (customer.status !== 'PENDING' && customer.status !== 'DRAFT' && customer.status !== 'REQUEST CHANGES') {
      this.customerService
        .downloadReport(customer.id)
        .subscribe(
          (res: any) => {
            const url = window.URL.createObjectURL(new Blob([res]));
            const link = document.createElement('a');
            const fileName = `Detailed Report - ${customer.name}.pdf`;
            link.href = url;
            link.setAttribute('download', fileName);
            document.body.appendChild(link);
            link.click();
        },
          (error: any) => {
          this.snackBar.error('Error: Failed to download detailed report.');
          }
        );
    }
  }

  downloadAcceptanceForm(customer: Customer): void {
    this.customerService
      .downloadAcceptanceForm(customer.id)
      .subscribe(
        (res: any) => {
          const url = window.URL.createObjectURL(new Blob([res]));
          const link = document.createElement('a');
          const fileName = `Customer Acceptance Form - ${customer.name}.pdf`;
          link.href = url;
          link.setAttribute('download', fileName);
          document.body.appendChild(link);
          link.click();
        },
        (error: any) => {
          this.snackBar.error('Error: Failed to download customer acceptance form.');
        }
      );
  }
}
